import { type ApolloLink, split } from '@apollo/client';

import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import type { EventClosedListener } from 'graphql-ws';
import { CloseCode, createClient } from 'graphql-ws';
import { createTreatyClient } from '../client';
import { MAX_REFRESH_ATTEMPTS, showLoginDialog } from './common';

let shouldRefreshToken = false;

const createBaseWSApolloLink = (
  hasuraUrl: string,
  headers?: Record<string, string>,
): ApolloLink => {
  const wsClient = createClient({
    url: hasuraUrl.replace(/^http/, 'ws'),
    connectionParams: async () => {
      if (shouldRefreshToken) {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          try {
            const response = await createTreatyClient().api.auth.refresh.post({
              refreshToken,
            });
            if (response.data && response.status === 200) {
              localStorage.setItem(
                'accessToken',
                response.data.data.accessToken,
              );
            } else {
              console.error('Token refresh failed:', response.error?.value);
              showLoginDialog();
            }
          } catch (error) {
            console.error('WS connection token refresh failed:', error);
            showLoginDialog();
            return;
          }
        } else {
          showLoginDialog();
          return;
        }
      }
      return {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken') || ''}`,
          ...headers,
        },
      };
    },
    shouldRetry: () => true,
    retryAttempts: MAX_REFRESH_ATTEMPTS,
    on: {
      closed: ((event) => {
        if ((event as { code: number }).code === CloseCode.Forbidden)
          shouldRefreshToken = true;
      }) as EventClosedListener,
      error: async (error: unknown) => {
        console.error('error', error);
        const message = error instanceof Error ? error.message : String(error);
        if (message.includes('jwt')) {
          const refreshToken = localStorage.getItem('refreshToken');
          if (refreshToken) {
            try {
              const response = await createTreatyClient().api.auth.refresh.post(
                {
                  refreshToken,
                },
              );
              if (response.data && response.status === 200) {
                localStorage.setItem(
                  'accessToken',
                  response.data.data.accessToken,
                );
              } else {
                console.error('Token refresh failed:', response.error?.value);
                showLoginDialog();
              }
            } catch (error) {
              console.error('WS connection token refresh failed:', error);
              showLoginDialog();
            }
          }
        }
      },
    },
  });

  const wsLink = new GraphQLWsLink(wsClient);

  return wsLink;
};

export const createWSApolloLink = (
  hasuraUrl: string,
  headers?: Record<string, string>,
): ApolloLink => {
  const splitLink = split(
    (query) => {
      const headers = query.getContext().headers;

      return (
        headers &&
        headers['x-hasura-role'] != null &&
        headers['x-hasura-role'] === 'user'
      );
    },
    createBaseWSApolloLink(hasuraUrl, {
      'x-hasura-role': 'user',
      ...headers,
    }),
    createBaseWSApolloLink(hasuraUrl, {
      ...headers,
    }),
  );

  return splitLink;
};
